import React, { useId, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import {
  Box,
  CircularProgress,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { Space } from '../../models/Space';

export interface HostTutorSpaceAssignmentFieldProps {
  tutorId: string;
  spaceId?: string;
  spaceName?: string;
  spaces: Space[];
  disabled?: boolean;
  onSpaceChange?: (tutorId: string, spaceId: string) => void;
}

const HostTutorSpaceAssignmentField: React.FC<HostTutorSpaceAssignmentFieldProps> = ({
  tutorId,
  spaceId,
  spaceName,
  spaces,
  disabled = false,
  onSpaceChange,
}) => {
  const { t } = useTranslation();
  const menuId = useId();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(anchorEl);

  const resolvedSpaceId = spaceId || '';
  const resolvedSpaceName =
    spaceName?.trim() ||
    spaces.find((space) => space.id === resolvedSpaceId)?.name?.trim() ||
    '';
  const canSwitch = spaces.length > 1 && !!onSpaceChange;
  const attachedSpaceLabel = t('hostMyTutors.attachedSpaceLabel', 'Attached to Space');
  const switchHint = t(
    'hostMyTutors.attachedSpaceSwitchHint',
    'Choose another space to move this tutor.'
  );

  if (!resolvedSpaceName && !canSwitch) {
    return null;
  }

  const closeMenu = () => setAnchorEl(null);

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (!canSwitch || disabled) {
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleSelectSpace = (nextSpaceId: string) => {
    closeMenu();
    if (nextSpaceId && nextSpaceId !== resolvedSpaceId && onSpaceChange) {
      onSpaceChange(tutorId, nextSpaceId);
    }
  };

  const spaceNameNode = canSwitch ? (
    <Typography
      component="button"
      type="button"
      variant="body2"
      disabled={disabled}
      onClick={openMenu}
      title={disabled ? resolvedSpaceName || undefined : switchHint}
      aria-label={attachedSpaceLabel}
      aria-haspopup="menu"
      aria-expanded={menuOpen}
      aria-controls={menuOpen ? menuId : undefined}
      sx={{
        color: 'primary.main',
        fontWeight: 600,
        verticalAlign: 'baseline',
        background: 'none',
        border: 0,
        p: 0,
        m: 0,
        font: 'inherit',
        textDecoration: 'underline',
        textUnderlineOffset: 2,
        cursor: disabled ? 'wait' : 'pointer',
        opacity: disabled ? 0.7 : 1,
        '&:hover': disabled ? undefined : { color: 'primary.light' },
      }}
    />
  ) : (
    <Typography
      component="span"
      variant="body2"
      title={resolvedSpaceName || undefined}
      sx={{
        color: 'primary.main',
        fontWeight: 600,
        verticalAlign: 'baseline',
      }}
    />
  );

  return (
    <>
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        useFlexGap
        flexWrap="wrap"
        sx={{ minWidth: 0, maxWidth: '100%' }}
      >
        <SchoolOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
        {resolvedSpaceName ? (
          <Typography variant="body2" color="text.secondary" component="span" sx={{ minWidth: 0 }}>
            <Trans
              i18nKey="tutorSearch.schoolAffiliation"
              defaults="Teaches at <spaceLink>{{name}}</spaceLink>"
              values={{ name: resolvedSpaceName }}
              components={{ spaceLink: spaceNameNode }}
            />
          </Typography>
        ) : (
          <Typography
            component="button"
            type="button"
            variant="body2"
            color="text.secondary"
            disabled={disabled}
            onClick={openMenu}
            sx={{
              background: 'none',
              border: 0,
              p: 0,
              m: 0,
              font: 'inherit',
              textDecoration: 'underline',
              textUnderlineOffset: 2,
              cursor: disabled ? 'wait' : 'pointer',
            }}
          >
            {t('hostMyTutors.selectSpacePlaceholder', 'Select a space')}
          </Typography>
        )}
        {canSwitch && disabled ? (
          <CircularProgress size={14} color="inherit" sx={{ color: 'text.secondary' }} />
        ) : null}
      </Stack>

      {canSwitch ? (
        <Menu
          id={menuId}
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={closeMenu}
          onClick={(event) => event.stopPropagation()}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: {
              sx: { minWidth: 200, maxWidth: 320 },
            },
          }}
        >
          <MenuItem disabled dense>
            <Typography variant="caption" color="text.secondary">
              {attachedSpaceLabel}
            </Typography>
          </MenuItem>
          {spaces.map((space) => {
            const selected = space.id === resolvedSpaceId;
            return (
              <MenuItem
                key={space.id}
                selected={selected}
                dense
                onClick={() => handleSelectSpace(space.id)}
                sx={{ gap: 1.5 }}
              >
                <ListItemText
                  primary={space.name}
                  primaryTypographyProps={{ noWrap: true }}
                  sx={{ flex: 1, minWidth: 0, my: 0 }}
                />
                <Box sx={{ width: 20, display: 'inline-flex', justifyContent: 'center', flexShrink: 0 }}>
                  {selected ? <CheckIcon fontSize="small" color="primary" /> : null}
                </Box>
              </MenuItem>
            );
          })}
        </Menu>
      ) : null}
    </>
  );
};

export default HostTutorSpaceAssignmentField;
